/**
 * Bulk Registration API
 * Requirements: 13.1, 13.5, 13.6, 13.9
 */
import type {
  BulkRegistrationResult,
  BulkRegistrationProgress,
} from '../types/bulkRegistration.types';
import { generateCSVTemplate } from '../utils/csvParser';
import { createAuthenticatedClient } from '../../../shared/api/axiosClient';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

let apiClient: ReturnType<typeof createAuthenticatedClient> | null = null;

const getApiClient = () => {
  if (!apiClient) {
    apiClient = createAuthenticatedClient(API_BASE_URL, () => {
      window.location.href = '/login';
    });
  }
  return apiClient;
};

/**
 * Bulk Registration API functions
 */
export const bulkRegistrationApi = {
  /**
   * Upload CSV data to register users
   * @param companyId - Company ID to register users under
   * @param csvData - CSV data containing user information
   * @param skipPasswordReset - If true, set password as permanent (no reset required on first login)
   */
  async uploadUsers(
    companyId: string,
    csvData: string,
    skipPasswordReset?: boolean
  ): Promise<BulkRegistrationResult> {
    const client = getApiClient();
    const response = await client.post<BulkRegistrationResult>(
      '/admin/bulk-registration',
      { companyId, csvData, skipPasswordReset }
    );
    return response.data;
  },

  /**
   * Get progress of a bulk registration job
   */
  async getProgress(jobId: string): Promise<BulkRegistrationProgress> {
    const client = getApiClient();
    const response = await client.get<BulkRegistrationProgress>(
      `/admin/bulk-registration/${jobId}/progress`
    );
    return response.data;
  },

  /**
   * Download CSV template
   */
  downloadTemplate(): void {
    const template = generateCSVTemplate();
    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'user_registration_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  },

  /**
   * Retry failed user registrations
   */
  async retryFailed(jobId: string): Promise<BulkRegistrationResult> {
    const client = getApiClient();
    const response = await client.post<BulkRegistrationResult>(
      `/admin/bulk-registration/${jobId}/retry`
    );
    return response.data;
  },
};
